import { useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';

const categories = ['All', 'Term Life', 'Cost & Pricing', 'Wealth Strategy Call', 'Life Insurance'];

// Lazy load FAQ data to reduce initial bundle size
const FAQContent = lazy(() => import('./FAQContent'));

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-20 md:py-24 bg-[#0f2236] relative overflow-hidden">
      {/* Decorative glows */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-[#D97642]/10 rounded-full blur-[100px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#2d5a8c]/20 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="relative max-w-[1100px] mx-auto px-4 sm:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-[#D97642]/20 text-[#f0a070] text-[12px] font-semibold uppercase tracking-widest rounded-full border border-[#D97642]/30 mb-5">
            FAQ
          </span>
          <h2 className="text-[28px] sm:text-[34px] md:text-[42px] font-bold text-white leading-tight">
            Life Insurance in Indianapolis — Straight Answers
          </h2>
          <p className="mt-4 text-[15px] md:text-[17px] text-white/60 max-w-[640px] mx-auto leading-relaxed">
            The most common questions Indiana families ask about coverage, cost, and how the process works — answered honestly and without jargon.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => { 
                setActiveCategory(cat); 
                setOpenIndex(null);
              }}
              className={`px-5 py-2 rounded-full text-[13px] md:text-[14px] font-medium transition-all cursor-pointer whitespace-nowrap border ${
                activeCategory === cat
                  ? 'bg-[#D97642] text-white border-[#D97642] shadow-md'
                  : 'bg-white/5 text-white/70 border-white/10 hover:border-[#D97642]/60 hover:text-white'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* FAQ Items - Lazy Loaded - Show Top 5 Only */}
        <Suspense fallback={
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-4 border-[#D97642] border-t-transparent rounded-full animate-spin"></div>
          </div>
        }>
          <FAQContent 
            activeCategory={activeCategory}
            openIndex={openIndex}
            setOpenIndex={setOpenIndex}
            limitToTop5={true}
          />
        </Suspense>

        {/* See All Questions Link */}
        <div className="text-center mt-10">
          <Link
            to="/faq"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/30 text-white text-[14px] font-semibold rounded-full hover:bg-white/20 transition-all whitespace-nowrap"
          >
            See All 31 Questions <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 bg-gradient-to-r from-[#D97642] to-[#C26638] rounded-2xl md:rounded-3xl px-6 md:px-12 py-10 md:py-12 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-8">
          <div>
            <h3 className="text-[20px] md:text-[26px] font-bold text-white leading-snug">
              Still have questions?
            </h3>
            <p className="text-[14px] md:text-[16px] text-white/80 mt-2">
              Benjamin is happy to answer any question about coverage, cost, or carriers — no obligation, no pressure, ever.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href="tel:+13176482983"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white text-[#D97642] text-[14px] md:text-[15px] font-semibold rounded-full hover:bg-gray-50 transition-all hover:shadow-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-[16px]"></i>
              Call (317) 648-2983
            </a>
            <Link
              to="/wealth-strategy-call"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/15 text-white text-[14px] md:text-[15px] font-semibold rounded-full hover:bg-white/25 transition-all border border-white/30 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-calendar-check-line text-[16px]"></i>
              Book a Free Call
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}